import { LayoutDashboard, LayoutGrid, Users, CreditCard, LogOut } from "lucide-react";
import { NavLink } from "react-router-dom";
import type { Me, Role } from "@opsbelt/contract";

import { OpsBeltMark } from "@/components/OpsBeltMark";
import { cn } from "@/lib/utils";

type NavItem = { to: string; label: string; icon: typeof Users };

/** Nav, gated by role — Billing is owner/admin only. */
function navFor(role: Role | undefined) {
  const canManage = role === "owner" || role === "admin";
  const org: NavItem[] = [{ to: "/team", label: "Team", icon: Users }];
  if (canManage) org.push({ to: "/billing", label: "Billing", icon: CreditCard });
  return [
    {
      label: "Workspace",
      items: [
        { to: "/", label: "Overview", icon: LayoutDashboard },
        { to: "/apps", label: "Apps", icon: LayoutGrid },
      ] as NavItem[],
    },
    { label: "Organisation", items: org },
  ];
}

const PLAN_LABEL: Record<string, string> = {
  "all-access": "All-Access",
  free: "Free",
};

export function Sidebar({ me, onSignOut }: { me?: Me; onSignOut: () => void }) {
  return (
    <aside className="flex h-full w-[248px] shrink-0 flex-col border-r border-border bg-card">
      {/* brand lockup — sits directly on the sidebar, no nested card */}
      <div className="flex items-center gap-[11px] border-b border-border px-[18px] py-[18px]">
        <OpsBeltMark size={28} className="shrink-0 text-primary" />
        <div className="min-w-0 leading-[1.15]">
          <div className="font-display text-[15px] font-bold tracking-tight">
            Ops<span className="text-primary">Belt</span>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            MSP toolkit
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-3">
        {navFor(me?.org.role).map((section) => (
          <div key={section.label}>
            {section.label && (
              <div className="px-2 pb-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                      )
                    }
                  >
                    <item.icon className="size-4" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <div className="mb-2 rounded-md bg-secondary/50 px-2.5 py-2">
          <div className="truncate text-sm font-medium">{me?.org.name ?? "—"}</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {me ? `${PLAN_LABEL[me.org.plan] ?? me.org.plan} plan` : ""}
          </div>
        </div>
        <div className="flex items-center justify-between px-1">
          <span className="truncate text-xs text-muted-foreground">
            {me?.user.email}
          </span>
          <button
            onClick={onSignOut}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
            type="button"
          >
            <LogOut className="size-3.5" />
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}
